/**
 * 내 비밀번호 변경 페이지
 * 
 * @description
 * 로그인한 사용자가 현재 비밀번호를 입력하고 새 비밀번호로 변경하는 페이지입니다.
 * 
 * @features
 * - 현재 비밀번호 입력 및 검증
 * - 새 비밀번호 입력 및 확인
 * - 비밀번호 유효성 검사 (최소 8자, 영문자+숫자 포함)
 * - 비밀번호 변경 완료 후 설정 페이지로 이동
 * - AlertDialog 통합 (성공/실패 메시지)
 * 
 * @api
 * - PATCH /user/my-password
 * 
 * @routes
 * - /settings/change-password (현재 페이지)
 * - /settings (비밀번호 변경 완료 후)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Input, AlertDialog } from '@/components/shared/ui';
import { ROUTES } from '@/lib/constants/routes';
import { useUser } from '@/data/queries/useUser';

export default function ChangeMyPassword(): React.ReactElement {
	const { t } = useTranslation('auth');
	// ========== API Hooks ==========
	const { changeMyPassword } = useUser();
	
	// ========== State 관리 ==========
	
	/**
	 * 현재 비밀번호 입력값
	 */
	const [currentPassword, setCurrentPassword] = useState<string>('');
	
	/**
	 * 새 비밀번호 입력값
	 */
	const [newPassword, setNewPassword] = useState<string>('');
	
	/**
	 * 비밀번호 확인 입력값
	 */
	const [confirmPassword, setConfirmPassword] = useState<string>('');
	
	/**
	 * 비밀번호 변경 진행 중 상태
	 * @description API 호출 중 버튼 비활성화
	 */
	const [isLoading, setIsLoading] = useState<boolean>(false);
	
	/**
	 * AlertDialog 상태
	 * @field isOpen - 팝업 열림 여부
	 * @field message - 표시할 메시지
	 */
	const [alertState, setAlertState] = useState<{ isOpen: boolean; message: string; }>({
		isOpen: false,
		message: '',
	});

	// ========== Hooks ==========
	
	const navigate = useNavigate();

	// ========== Helper 함수 ==========
	
	/**
	 * AlertDialog 표시
	 * @param message - 표시할 메시지
	 * @description AlertDialog를 열고 메시지를 설정
	 */
	const showAlert = (message: string): void => {
		setAlertState({
			isOpen: true,
			message,
		});
	};

	/**
	 * AlertDialog 닫기
	 * @description AlertDialog를 닫음
	 */
	const closeAlert = (): void => {
		setAlertState({
			isOpen: false,
			message: '',
		});
	};

	/**
	 * 비밀번호 유효성 검사
	 * @param pw - 검사할 비밀번호
	 * @returns 유효성 검사 결과 (true: 유효, false: 무효)
	 * @description 최소 8자 이상, 영문자와 숫자 포함 여부 확인
	 */
	const validatePassword = (pw: string): boolean => {
		if (pw.length < 8) {
			return false;
		}
		// 영문자와 숫자 포함 여부 확인
		const hasLetter = /[a-zA-Z]/.test(pw);
		const hasNumber = /[0-9]/.test(pw);
		return hasLetter && hasNumber;
	};

	// ========== Event Handlers ==========
	
	/**
	 * 폼 제출 핸들러
	 * @param e - 폼 제출 이벤트
	 * @description 현재 비밀번호와 새 비밀번호를 입력받아 비밀번호 변경 처리
	 * 
	 * @process
	 * 1. 현재 비밀번호 입력 여부 확인
	 * 2. 새 비밀번호 입력 여부 확인
	 * 3. 비밀번호 확인 입력 여부 확인
	 * 4. 비밀번호 일치 확인
	 * 5. 비밀번호 유효성 검사 (최소 8자, 영문자+숫자)
	 * 6. 비밀번호 변경 API 호출
	 * 7. 성공 시 설정 페이지로 이동
	 */
	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
		e.preventDefault();

		// 현재 비밀번호 입력 여부 확인
		if (!currentPassword.trim()) {
			showAlert(t('changeMyPassword.alertCurrentPasswordRequired'));
			return;
		}

		// 새 비밀번호 입력 여부 확인
		if (!newPassword.trim()) {
			showAlert(t('changeMyPassword.alertNewPasswordRequired'));
			return;
		}

		// 비밀번호 확인 입력 여부 확인
		if (!confirmPassword.trim()) {
			showAlert(t('changeMyPassword.alertPasswordConfirmRequired'));
			return;
		}

		// 비밀번호 일치 확인
		if (newPassword !== confirmPassword) {
			showAlert(t('changeMyPassword.alertPasswordMismatch'));
			return;
		}

		// 비밀번호 유효성 검사 (최소 8자, 영문자+숫자 포함)
		if (!validatePassword(newPassword)) {
			showAlert(t('changeMyPassword.alertPasswordRules'));
			return;
		}

		// 현재 비밀번호와 새 비밀번호가 같은지 확인
		if (currentPassword === newPassword) {
			showAlert(t('changeMyPassword.alertSamePassword'));
			return;
		}

		try {
			setIsLoading(true);

			await changeMyPassword({
				currentPassword: currentPassword.trim(),
				newPassword: newPassword.trim(),
			});
		
		// 성공 메시지 표시
		showAlert(t('changeMyPassword.alertSuccess'));
		
		// 1.5초 후 홈으로 이동
			setTimeout(() => {
				navigate(ROUTES.HOME);
			}, 1500);
		} catch (error: any) {
			showAlert(error.message || t('changeMyPassword.alertError'));
		} finally {
			// 항상 실행: 로딩 상태 종료
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
			<div className="w-full max-w-[460px] bg-white rounded-3xl p-12 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)] border border-gray-100">
				{/* Header */}
				<div className="text-center mb-8">
					<div className="mb-6 flex justify-center">
						<img
							src="/assets/images/brand/medicrew_mini_logo.png"
							alt="medicrew"
							className="w-16 h-16 object-contain"
						/>
					</div>
					<h1 className="text-2xl font-semibold text-brand-500 mb-4">
						{t('changeMyPassword.pageTitle')}
					</h1>
					<p className="text-gray-600 text-base leading-relaxed mb-6">
						{t('changeMyPassword.description')}
					</p>
				</div>

				{/* Form */}
				<form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
					<div>
						<label className="block mb-1 text-sm font-semibold text-gray-900 flex items-center gap-1">
							{t('changeMyPassword.currentPasswordLabel')} <span className="text-red-500">*</span>
						</label>
						<Input
							type="password"
							className="border-gray-200 rounded-xl"
							placeholder={t('changeMyPassword.currentPasswordPlaceholder')}
							value={currentPassword}
							onChange={(e) => setCurrentPassword(e.target.value)}
							disabled={isLoading}
						/>
					</div>

					<div>
						<label className="block mb-1 text-sm font-semibold text-gray-900 flex items-center gap-1">
							{t('changeMyPassword.newPasswordLabel')} <span className="text-red-500">*</span>
						</label>
						<Input
							type="password"
							className="border-gray-200 rounded-xl"
							placeholder={t('changeMyPassword.newPasswordPlaceholder')}
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							disabled={isLoading}
						/>
						<p className="text-xs text-gray-900 mt-1">{t('changeMyPassword.hint')}</p>
					</div>

					<div>
						<label className="block mb-2 text-sm font-semibold text-gray-900">
							{t('changeMyPassword.passwordConfirmLabel')} <span className="text-red-500">*</span>
						</label>
						<Input
							type="password"
							className="border-gray-200 rounded-xl"
							placeholder={t('changeMyPassword.passwordConfirmPlaceholder')}
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							disabled={isLoading}
						/>
					</div>

					<div className="flex gap-3 mt-4">
						<Button
							type="submit"
							className="flex-1 rounded-full text-base active:scale-95 h-10"
							variant="primary"
							disabled={isLoading}
						>
							{isLoading ? t('changeMyPassword.submitting') : t('changeMyPassword.submitButton')}
						</Button>
					<Button
						type="button"
						onClick={() => navigate(ROUTES.HOME)}
						className="flex-1 rounded-full text-base active:scale-95 h-10"
						variant="secondary"
						disabled={isLoading}
					>
						{t('changeMyPassword.cancelButton')}
					</Button>
					</div>
				</form>
			</div>

			{/* Powered By */}
			<div className="mt-8 flex justify-center items-center gap-2">
				<span className="text-xs text-gray-800">Powered by</span>
				<span className="text-sm font-bold text-gray-800">NEWBASE</span>
			</div>

			{/* AlertDialog */}
			<AlertDialog
				isOpen={alertState.isOpen}
				onClose={closeAlert}
				message={alertState.message}
			/>
		</div>
	);
}
